import * as THREE from 'three';
import type { WorldConfig } from '../config/world';
import type { WorldState } from '../world/state/types';
import { EmissiveController } from './emissive';
import type { Placement } from './composition';
import { buildGround, buildHillTrees, buildIslands, buildRidges, buildSugi, buildTown, buildWires, updateTown, type Town } from './far/builders';
import { azAltToDirection, type Vec3 } from './frame';
import { Lighting, emissiveCompensation, lightLevels, type LightLevels } from './Lighting';
import { cloudLayers, cloudOffsets } from './sky/cloudParams';
import { Moon } from './sky/Moon';
import { SkyRenderer } from './sky/SkyRenderer';
import { lanternLevel } from './emissive';
import { buildFence } from './fence';
import { buildMorningFog, updateMorningFog } from './far/morningFog';
import { Sea } from './sea/Sea';
import { MATERIAL_UNIFORMS, patchTree } from './materials';
import { SurfaceEffects } from './SurfaceEffects';
import { buildLeafCards } from './vegetation/leafCards';
import { buildLane, buildTerraceFloor, buildTerraceWalls, buildTerrain } from './ground/terrain';
import { buildStairs } from './ground/stairs';

/** Assembles sky, lights, far planes, ground and the placed Blender assets. */
export class WorldScene {
  readonly scene = new THREE.Scene();
  readonly emissive = new EmissiveController();
  readonly surfaces = new SurfaceEffects();
  readonly lighting = new Lighting();
  readonly sky: SkyRenderer;
  readonly moonDisc = new Moon();
  private readonly town: Town;
  private readonly fog = buildMorningFog();
  private readonly sea: Sea;
  private readonly lamps: THREE.PointLight[] = [];

  constructor(
    private readonly config: WorldConfig,
    renderer: THREE.WebGLRenderer,
  ) {
    this.town = buildTown(config.seed);
    this.sky = new SkyRenderer(renderer);
    this.sea = new Sea(this.sky, config.seed, config.toriiAzimuthDeg);
    this.lighting.addTo(this.scene);
    this.scene.add(this.sky.dome, this.sky.probe, this.moonDisc.mesh, this.fog, this.sea.mesh);
    MATERIAL_UNIFORMS.uAerialSky.value = this.sky.skySun.texture;
    MATERIAL_UNIFORMS.uAerialSkyMoon.value = this.sky.skyMoon.texture;
    this.lighting.sun.castShadow = true;
    const sc = this.lighting.sun.shadow.camera;
    sc.left = -26; sc.right = 26; sc.top = 26; sc.bottom = -26; sc.near = 1; sc.far = 260;
    this.lighting.sun.shadow.bias = -0.0004;
    this.lighting.sun.shadow.normalBias = 0.03;
    this.buildWorld();
  }

  /** Adds a loaded asset (GLB or stub) at its composition placement; wires emissive + snow. */
  place(asset: THREE.Object3D, placement: Placement): void {
    asset.position.set(...placement.position);
    asset.rotation.y = (placement.rotationYDeg * Math.PI) / 180;
    asset.updateMatrixWorld(true);
    this.emissive.collect(asset);
    this.surfaces.apply(asset);
    this.scene.add(asset);
  }

  /** Leaf cards around the placed sakura's tips. */
  addFoliage(sakura: THREE.Object3D): void {
    this.scene.add(buildLeafCards(sakura, this.config.seed));
  }

  /** Wires between the placed poles' anchor empties, in placement order. */
  addWires(poles: THREE.Object3D[]): void {
    const anchor = (pole: THREE.Object3D, name: string): Vec3 => {
      const p = (pole.getObjectByName(name) ?? pole).getWorldPosition(new THREE.Vector3());
      return [p.x, p.y, p.z];
    };
    this.scene.add(buildWires(poles.map((p) => [anchor(p, 'utility_pole_wire_a'), anchor(p, 'utility_pole_wire_b')])));
  }

  /** Terrace fence from the fence bay asset. */
  addFence(segment: THREE.Object3D): void {
    const fence = buildFence(segment);
    this.surfaces.apply(fence);
    this.scene.add(fence);
  }

  /**
   * Call once everything is placed: aerial perspective on every material (single patch point), shadow flags,
   * and real point lights in the lanterns nearest the camera.
   */
  finalize(camera: THREE.Camera): void {
    patchTree(this.scene, { aerial: true }, (o) => o.name === 'sky' || o.name === 'moon' || o.name === 'sea' || o.name === 'morning_fog');
    this.scene.traverse((o) => {
      if (!(o instanceof THREE.Mesh) || o.name === 'sky' || o.name === 'moon' || o.name === 'morning_fog') return;
      const near = o.getWorldPosition(new THREE.Vector3()).distanceTo(camera.position) < 60;
      o.castShadow = near && !(o instanceof THREE.InstancedMesh);
      o.receiveShadow = near || o.name.startsWith('ground');
    });
    const lanterns = this.emissive.positions('lantern').sort((a, b) => a.distanceTo(camera.position) - b.distanceTo(camera.position));
    for (const p of lanterns.slice(0, 3)) {
      const l = new THREE.PointLight(0xffb566, 0, 9, 2);
      l.position.copy(p);
      this.lamps.push(l);
      this.scene.add(l);
    }
  }

  /** Neutral grey everywhere except the sky: composition review (checkpoint K1). */
  applyClay(): void {
    const clay = new THREE.MeshStandardMaterial({ color: 0x9a9a9a, roughness: 0.9, side: THREE.DoubleSide });
    this.scene.traverse((o) => {
      if (o.name !== 'sky' && o.name !== 'moon' && o.name !== 'morning_fog' && o.name !== 'sea' && (o instanceof THREE.Mesh || o instanceof THREE.InstancedMesh)) o.material = clay;
    });
  }

  update(state: WorldState, animTime: number, camera: THREE.Camera, nowMs: number): LightLevels {
    const { sun, moon } = state.sky;
    const view = this.config.cameraAzimuthDeg;
    const sunDir = azAltToDirection(sun.azDeg, sun.altDeg, view);
    const moonDir = azAltToDirection(moon.azDeg, moon.altDeg, view);
    const levels = lightLevels(state);
    const clouds = cloudLayers(state.weather);
    this.sky.update(
      {
        sunDir,
        moonDir,
        sunIlluminance: levels.skySun,
        moonIlluminance: levels.skyMoon,
        townGlow: levels.townGlow,
        clouds,
        cloudOffsets: cloudOffsets(clouds, animTime),
        cloudDetail: 4,
      },
      camera,
      nowMs,
    );
    // the lit limb sits just under the bloom threshold (1.6 after exposure): a bright disc whose maria still read,
    // not a blown-out white blob
    this.moonDisc.update(camera.position, moonDir, sunDir, moon.altDeg, moon.illuminatedFraction, 1.4 / levels.exposure);
    this.lighting.update(levels, new THREE.Vector3(...sunDir), new THREE.Vector3(...moonDir));
    const comp = emissiveCompensation(levels.exposure);
    this.emissive.update(state, comp);
    this.surfaces.update(state);
    updateTown(this.town, state, this.config.seed, animTime, comp);
    MATERIAL_UNIFORMS.uAerialSunI.value = levels.skySun;
    MATERIAL_UNIFORMS.uAerialMoonI.value = levels.skyMoon;
    MATERIAL_UNIFORMS.uAerialBright.value = 0.4 + 0.6 * Math.min(1, Math.max(0, (sun.altDeg + 6) / 12));
    const haze = this.sky.probe.sh.getAt(new THREE.Vector3(0, 0.06, -1).normalize(), new THREE.Vector3());
    updateMorningFog(this.fog, state, new THREE.Color(haze.x, haze.y, haze.z).multiplyScalar(this.sky.probe.intensity * MATERIAL_UNIFORMS.uAerialBright.value));
    this.sea.update(state, levels, sunDir, moonDir, view, animTime);
    const lampLevel = lanternLevel(sun.altDeg) * comp;
    for (const l of this.lamps) l.intensity = 9 * lampLevel;
    return levels;
  }

  private buildWorld(): void {
    const stairs = buildStairs().group;
    this.emissive.collect(stairs);
    this.scene.add(
      buildTerrain(), buildTerraceFloor(), buildTerraceWalls(), buildLane(), stairs,
      buildGround(), buildRidges(this.config.seed), buildIslands(this.config.seed, this.config.toriiAzimuthDeg), buildSugi(this.config.seed), buildHillTrees(this.config.seed), this.town.group,
    );
    this.surfaces.apply(this.scene);
  }
}
